import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { CALENDLY_URL } from "@/i18n";
import { scrollToId } from "@/utils";
import { EASE } from "@/components/Reveal";

export const LangToggle = ({ onDark = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="nav-lang-toggle"
      className={`flex items-center rounded-full border p-0.5 ${
        onDark ? "border-white/20" : "border-noom-line"
      }`}
    >
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`nav-lang-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
            lang === l
              ? onDark
                ? "bg-noom-lilac text-noom-ink"
                : "bg-noom-ink text-white"
              : onDark
                ? "text-white/50 hover:text-white"
                : "text-noom-faint hover:text-noom-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const BrandMark = ({ onDark = false }) => (
  <button
    data-testid="nav-brand-logo"
    onClick={() => scrollToId("#top")}
    aria-label="studio noom — torna all'inizio"
    className="flex items-center"
  >
    <img
      src={onDark ? "/assets/logo-light-wide.png" : "/assets/logo-dark-wide.png"}
      alt="studio noom"
      className="h-6 w-auto lg:h-7"
    />
  </button>
);

const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 350 : 0);
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-noom-line bg-white/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-6 lg:h-20 lg:px-16">
          <BrandMark />
          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
            {t.nav.links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id.slice(1)}`}
                onClick={() => go(l.id)}
                className="group flex items-baseline gap-1.5 text-sm text-noom-muted transition-colors duration-300 hover:text-noom-ink"
              >
                <span className="font-mono text-[10px] text-noom-lilacDeep">{l.num}</span>
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle />
            <a
              data-testid="nav-book-call-button"
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="group hidden items-center gap-2 rounded-full bg-noom-ink px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-noom-lilac hover:text-noom-ink md:inline-flex"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              data-testid="nav-menu-button"
              onClick={() => setOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-noom-line text-noom-ink lg:hidden"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex flex-col bg-noom-ink text-white"
          >
            <div className="flex h-16 items-center justify-between px-6">
              <BrandMark onDark />
              <button
                data-testid="drawer-close-button"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6">
              {t.nav.links.map((l, i) => (
                <span key={l.id} className="block overflow-hidden">
                  <motion.button
                    data-testid={`drawer-link-${l.id.slice(1)}`}
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.7, delay: 0.08 + i * 0.07, ease: EASE }}
                    onClick={() => go(l.id)}
                    className="flex items-baseline gap-4 py-2 text-left font-display text-4xl font-semibold tracking-tight"
                  >
                    <span className="font-mono text-xs text-noom-lilac">{l.num}</span>
                    {l.label}
                  </motion.button>
                </span>
              ))}
            </nav>
            <div className="flex items-center justify-between border-t border-white/10 px-6 py-6">
              <LangToggle onDark />
              <a
                data-testid="drawer-book-call-button"
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-noom-lilac px-5 py-2.5 text-sm font-semibold text-noom-ink"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
